import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  timestamp,
  varchar,
  text,
  integer,
  bigint,
  boolean,
  uuid,
  jsonb,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { organizations } from './organizations';
import { users } from './users';

/**
 * Invites (beta invites, beta codes, and firm/team invites) share one MANDATORY,
 * fixed 7-day expiry window. It is always computed on the server as `now + 7 days` at
 * create / resend / re-activate time; any client-supplied `expiresAt` is ignored. This
 * is the single source of truth for the duration. For beta invites and codes a `null`
 * expiresAt only ever occurs on legacy rows written before this policy and is treated
 * as already expired everywhere it is validated (fail closed); firm invites have always
 * been created with a server-set expiry, so they have no null-expiry legacy population.
 */
export const INVITE_EXPIRY_DAYS = 7;
export const INVITE_EXPIRY_MS = INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/** Server-authoritative expiry for a new / resent / re-activated invite or code. */
export function inviteExpiryFrom(now: Date = new Date()): Date {
  return new Date(now.getTime() + INVITE_EXPIRY_MS);
}

/** Beta-named aliases of the generic invite-expiry symbols above (same 7-day window). */
export const BETA_EXPIRY_DAYS = INVITE_EXPIRY_DAYS;
export const BETA_EXPIRY_MS = INVITE_EXPIRY_MS;
export const betaExpiryFrom = inviteExpiryFrom;

export const betaInvites = pgTable('beta_invites', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  email: varchar('email', { length: 320 }).notNull(),
  invitedBy: varchar('invited_by').references(() => users.id, { onDelete: 'set null' }),
  note: text('note'),
  usedAt: timestamp('used_at'),
  // Superadmin beta-invite management: revoke, expiry, and resend tracking.
  revokedAt: timestamp('revoked_at'),
  expiresAt: timestamp('expires_at'),
  lastSentAt: timestamp('last_sent_at'),
  sendCount: integer('send_count').notNull().default(0),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('beta_invites_email_unique').on(table.email),
]);

export const betaCodes = pgTable('beta_codes', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  code: varchar('code', { length: 64 }).notNull(),
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  maxUses: integer('max_uses').notNull().default(1),
  useCount: integer('use_count').notNull().default(0),
  isActive: boolean('is_active').notNull().default(true),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('beta_codes_code_unique').on(table.code),
]);

export const firmInvites = pgTable('firm_invites', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  email: varchar('email', { length: 320 }).notNull(),
  token: varchar('token', { length: 128 }).notNull(),
  hasClaimsAccess: boolean('has_claims_access').notNull().default(true),
  canManageBilling: boolean('can_manage_billing').notNull().default(false),
  canAccessAccounting: boolean('can_access_accounting').notNull().default(false),
  invitedBy: varchar('invited_by').references(() => users.id, { onDelete: 'set null' }),
  inviteLink: text('invite_link'),
  acceptedAt: timestamp('accepted_at'),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('firm_invites_token_unique').on(table.token),
  index('firm_invites_org_id_idx').on(table.orgId),
  index('firm_invites_email_idx').on(table.email),
]);

export const stripeWebhookEvents = pgTable('stripe_webhook_events', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  stripeEventId: varchar('stripe_event_id', { length: 255 }).notNull(),
  orgId: uuid('org_id').references(() => organizations.id, { onDelete: 'cascade' }),
  type: varchar('type', { length: 128 }).notNull(),
  processedAt: timestamp('processed_at').defaultNow().notNull(),
  payload: jsonb('payload'),
}, (table) => [
  uniqueIndex('stripe_webhook_events_event_id_unique').on(table.stripeEventId),
  index('stripe_webhook_events_org_id_idx').on(table.orgId),
]);

export type BetaInvite = typeof betaInvites.$inferSelect;
export type InsertBetaInvite = typeof betaInvites.$inferInsert;
export type BetaCode = typeof betaCodes.$inferSelect;
export type InsertBetaCode = typeof betaCodes.$inferInsert;
export type FirmInvite = typeof firmInvites.$inferSelect;
export type InsertFirmInvite = typeof firmInvites.$inferInsert;
export type StripeWebhookEvent = typeof stripeWebhookEvents.$inferSelect;
export type InsertStripeWebhookEvent = typeof stripeWebhookEvents.$inferInsert;

// =============================================================================
// Wallets & token ledger (AI spend billing, PR 1 of 6 — schema + service only)
// =============================================================================
//
// MODEL (owner decisions):
//   * Every user has ONE wallet. It holds a $100 signup grant given ONCE per user
//     EVER (across all firms), plus the user's own top-ups, plus tokens the user's
//     firm has allocated to them. AI spend always debits a USER wallet.
//   * A firm has ONE token balance. A firm owner/admin buys tokens into it and
//     allocates them out to users in the SAME firm. The firm balance is never spent
//     on AI directly and never goes negative.
//   * No reclaim in v1. When a user leaves a firm, the allocated remainder returns
//     to the firm and the user's own grant+top-ups are forfeited (see walletService).
//
// MONEY UNITS: integer micros of USD (1 USD = 1_000_000 micros). The $100 grant is
// 100_000_000 micros. bigint columns with mode:'number' — there is no existing
// bigint column in the schema to follow, and every realistic wallet/firm balance
// (even $1,000,000 = 1e12 micros) is far below Number.MAX_SAFE_INTEGER (2^53 ≈
// 9.007e15 micros ≈ $9.007e9), so JS numbers are lossless here and keep the service
// arithmetic in plain integers. mode:'bigint' would force BigInt literals through
// the whole service and every test for no practical gain at these magnitudes.
// walletService rejects any amount that is not a positive safe integer.
//
// RLS / DDL LIVES IN THE MANUAL MIGRATION, NOT HERE. Like contact_methods (0106),
// these tables are created by the hand-run migration drizzle/manual/0110_wallet-ledger.sql
// (NOT journaled — the drizzle journal is out of step with drizzle/, MIS-86 — and
// NOT applied by CI's db:migrate / verify-migrations-db job). The per-firm Row Level
// Security, CHECK constraints and the wallet_ledger append-only trigger are defined
// there. These Drizzle definitions stay plain (columns, FKs, indexes) and MUST be
// kept in step with that SQL; the exported scope/kind arrays mirror its CHECKs.

/** 1 USD = 1,000,000 micros. All wallet money is stored as integer micros of USD. */
export const WALLET_MICROS_PER_USD = 1_000_000;
/** The one-time signup grant: $100, in micros. Granted once per user EVER. */
export const SIGNUP_GRANT_MICROS = 100 * WALLET_MICROS_PER_USD;

/** wallet_ledger.scope — whether a row moves a user wallet or a firm balance. Mirrors the 0110 CHECK. */
export const WALLET_LEDGER_SCOPES = ['user', 'firm'] as const;
export type WalletLedgerScope = (typeof WALLET_LEDGER_SCOPES)[number];

/** wallet_ledger.kind — the economic event. Mirrors the 0110 CHECK. */
export const WALLET_LEDGER_KINDS = [
  'grant', // +user: the one-time $100 signup grant
  'debit', // -user: AI spend
  'topup', // +user: the user's own Stripe top-up
  'firm_purchase', // +firm: the firm buying tokens into its balance
  'allocate_out', // -firm: tokens leaving the firm balance toward a user
  'allocate_in', // +user: tokens arriving in a user wallet from the firm (paired with allocate_out)
  'forfeit', // -user: the user's own remainder, lost when they leave the firm
  'return_to_firm', // +firm / -user: the allocated remainder going back to the firm when the user leaves
  'adjust', // signed: manual correction, and the write-off of an overdrawn wallet on leave
] as const;
export type WalletLedgerKind = (typeof WALLET_LEDGER_KINDS)[number];

/**
 * One wallet per user. balance_micros is the spendable total; allocated_micros is
 * the portion of balance that came from a firm allocation (so balance - allocated
 * is the user's OWN money — grant + top-ups). The split is needed by userLeftFirm:
 * the allocated remainder returns to the firm, the own remainder is forfeited.
 * org_id is the user's current firm (RLS scope). CHECK (allocated_micros >= 0) and
 * the per-firm RLS are in drizzle/manual/0110.
 */
export const userWallets = pgTable('user_wallets', {
  userId: varchar('user_id').primaryKey().references(() => users.id, { onDelete: 'cascade' }),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  balanceMicros: bigint('balance_micros', { mode: 'number' }).notNull().default(0),
  allocatedMicros: bigint('allocated_micros', { mode: 'number' }).notNull().default(0),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index('idx_user_wallets_org_id').on(table.orgId),
]);

/**
 * One token balance per firm. A firm owner/admin buys tokens in (firm_purchase) and
 * allocates them out to users (allocate_out). Never spent on AI directly and never
 * negative — CHECK (balance_micros >= 0) and the per-firm RLS are in drizzle/manual/0110.
 */
export const firmTokenBalances = pgTable('firm_token_balances', {
  orgId: uuid('org_id').primaryKey().references(() => organizations.id, { onDelete: 'cascade' }),
  balanceMicros: bigint('balance_micros', { mode: 'number' }).notNull().default(0),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
});

/**
 * Append-only ledger: one row per balance movement, the audit trail behind every
 * wallet/firm balance. amount_micros is SIGNED (debits/forfeits are negative).
 * Every row carries an idempotency_key (UNIQUE, NOT NULL) — walletService inserts
 * ON CONFLICT DO NOTHING so a replayed call changes nothing. stripe_event_id is
 * UNIQUE (nullable; Postgres allows many NULLs) for Stripe-driven rows.
 *
 * org_id has NO foreign key on purpose: an immutable financial audit row must
 * survive an org hard-delete and must never be cascade-deleted (the append-only
 * trigger in 0110 would block an org's ON DELETE CASCADE anyway). user_id also has
 * no FK — a ledger row must never fail on the user it names (mirrors llm_usage_ledger).
 *
 * Append-only is enforced in 0110 by SELECT+INSERT RLS policies only (no UPDATE/DELETE
 * policy) PLUS a BEFORE UPDATE OR DELETE trigger that RAISEs — so even the table
 * owner cannot mutate or delete a row.
 */
export const walletLedger = pgTable('wallet_ledger', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull(),
  userId: varchar('user_id'),
  scope: text('scope').notNull(),
  kind: text('kind').notNull(),
  amountMicros: bigint('amount_micros', { mode: 'number' }).notNull(),
  llmUsageLedgerId: uuid('llm_usage_ledger_id'),
  stripeEventId: text('stripe_event_id'),
  idempotencyKey: text('idempotency_key').notNull(),
  actorUserId: varchar('actor_user_id'),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  uniqueIndex('wallet_ledger_idempotency_key_uq').on(table.idempotencyKey),
  uniqueIndex('wallet_ledger_stripe_event_id_uq').on(table.stripeEventId),
  index('idx_wallet_ledger_org_created').on(table.orgId, table.createdAt),
  index('idx_wallet_ledger_user_created').on(table.userId, table.createdAt),
]);

export type UserWallet = typeof userWallets.$inferSelect;
export type InsertUserWallet = typeof userWallets.$inferInsert;
export type FirmTokenBalance = typeof firmTokenBalances.$inferSelect;
export type InsertFirmTokenBalance = typeof firmTokenBalances.$inferInsert;
export type WalletLedgerRow = typeof walletLedger.$inferSelect;
export type InsertWalletLedgerRow = typeof walletLedger.$inferInsert;
